import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import classNames from "classnames";
import { Entry } from "contentful";
import { useRouter } from "next/router";
import { AbstractIntlMessages, useTranslations } from "next-intl";
import { CSSProperties, useMemo } from "react";

import CrossCountry from "~/types/crossCountry";
import Editorials from "~/types/editorials";
import textUtils from "~/utils/text-utils";

import countries from "../../../public/json/countries.json";
import CountryDialog from "../common/country-dialog";
import Icon from "../common/icon";
import SeoHead from "../common/seo-head";
import Header from "./header";
import MiniFooter from "./mini-footer";

type Props = {
  children: JSX.Element;
  navigation: Entry<unknown>;
  footer: Entry<unknown>;
  page?: Editorials.BasicPageTemplate | null;
  navColor?: Editorials.navColor;
  inPpd?: boolean;
  mainStyle?: CSSProperties;
  mainClasses?: string;
  miniFooter?: boolean;
  root?: CrossCountry.DetailedProduct;
  isHomepage?: boolean;
  productChildren?: CrossCountry.Product[];
  messages?: AbstractIntlMessages;
};

export default function LayoutPage(props: Props) {
  const router = useRouter();
  const t = useTranslations();
  const isProductPage = router.pathname.includes("/p/") && !!props.root;

  const liveEntryHeader = useContentfulLiveUpdates(props.navigation);
  const liveEntryFooter = useContentfulLiveUpdates(props.footer);

  const seoProductDescription = useMemo(() => {
    if (props.root?.short_description) {
      const escapedShortDesc = textUtils.escapeHtmlParagraph(
        props.root.short_description
      );
      return `${props.root?.marketing_description || escapedShortDesc}. ${t(
        "generic.buyNow"
      )}`;
    }
    return;
  }, [props.root, t]);

  const allUnifiedCommerceLocales = useMemo(
    () =>
      countries.continents
        .flatMap((c) => c.countries)
        .filter((c) => c.isUnifiedCommerce)
        .flatMap((c) => c.cultures.map((c) => c.locale)),
    []
  );

  function canonicalUrl() {
    const baseUrl = `https://www.kikocosmetics.com/${router.locale}`;

    if (props.root?.product_id) {
      const slug = props.productChildren?.find((child) => child.isCanonical)
        ? props.productChildren.find((child) => child.isCanonical)?.slug
        : props.root.slug;
      const productId = props.productChildren?.find(
        (child) => child.isCanonical
      )
        ? props.productChildren.find((child) => child.isCanonical)?.product_id
        : props.root.product_id;
      return `${baseUrl}/p/${slug}-${productId}/`;
    }

    return `${baseUrl}${router.asPath.split("?", 1)[0]}`;
  }

  return (
    <>
      {props.page?.seoMetadata && (
        <SeoHead
          entry={props.page.seoMetadata}
          productDetail={props.root ? props.root : undefined}
          titleSuffix={t("generic.pageTitleSuffix")}
          productDescription={seoProductDescription}
          canonical={canonicalUrl()}
        />
      )}
      <div style={{ position: "absolute", opacity: 0 }} aria-hidden="true">
        <Icon name="heart-path" />
      </div>
      <Header
        entry={liveEntryHeader}
        navColor={
          props.page?.navColor
            ? props.page.navColor
            : props.navColor || "transparent"
        }
        inPpd={props.inPpd}
        isHomepage={props.isHomepage}
        messages={props.messages}
      />
      <main
        className={classNames(
          { contentBelowHeader: props.page?.contentBelowHeader || false },
          props.mainClasses
        )}
        style={props.mainStyle}
      >
        {props.children}
      </main>
      <CountryDialog />
      <MiniFooter entry={liveEntryFooter} />
    </>
  );
}
